import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';
import { usePublicForm } from '../hooks/usePublicContent';
import { submitLead } from '../services/leadService';
import { readOrigin } from '../utils/utm';
import { DynamicForm, DynamicFormResult } from '../components/public/DynamicForm';
import { Button } from '../components/ui/Button';
import { BrandLogo } from '../components/shared/BrandLogo';

/** Formulario público independiente: /formulario/:slug */
export function PublicForm() {
  const {
    slug = ''
  } = useParams();
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = usePublicForm(slug);
  const form = db.forms.find(f => f.slug === slug);
  const organization = form ? db.organizations.find(o => o.id === form.organizationId) : undefined;
  const logoUrl = organization?.brand.logoUrl ?? organization?.logoUrl;
  if (loading && !form) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-surface" aria-busy="true" data-id="element-1491" data-mp="3qt1tjcaoqjgj">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1492" data-mp="3gwp1sjypafof" />
        <span className="sr-only" data-id="element-1493" data-mp="3by41kkjg7vzz">Cargando el formulario…</span>
      </main>;
  }
  if (!form) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-1494" data-mp="dghxqccdigf4">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-1495" data-mp="8igjc4pw759z" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-1496" data-mp="3pnbh7z4ngto">
          Formulario no disponible
        </h1>
        <Link to="/" data-id="element-1497" data-mp="3uxhy9a8kbku6">
          <Button variant="secondary" data-id="element-1498" data-mp="3uw6wffyogn5p">Volver al inicio</Button>
        </Link>
      </main>;
  }
  async function handleSubmit(result: DynamicFormResult) {
    if (!form) return;
    await submitLead({
      organizationId: form.organizationId,
      formId: form.id,
      contact: result.contact,
      answers: result.answers,
      origin: readOrigin(location.search, {
        formId: form.id
      })
    });
    navigate(`/gracias/${form.thankYouSlug ?? form.slug}`);
  }
  return <main className="min-h-screen w-full bg-surface px-5 py-16 sm:py-24" data-id="element-1499" data-mp="2bytqq9w42o8f">
      <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-1500" data-mp="2gwvdp5wnr7p6">
        <div className="mb-10 flex justify-center" data-id="element-1501" data-mp="2gvajjhe99qru">
          <BrandLogo src={logoUrl} size={30} data-id="element-1502" data-mp="22x437f5co9fx" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-1503" data-mp="22zq9lbgi8iwk">
          {form.name}
        </h1>
        {form.description && <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-1504" data-mp="1t07qsycbz363">
            {form.description}
          </p>}
      </div>
      <DynamicForm form={form} onSubmit={handleSubmit} data-id="element-1505" data-mp="1y0dczyztzxgs" />
    </main>;
}